import { AuthConfig } from 'angular-oauth2-oidc';

const ENVIRONMENT_NAMES = [
  'development',
  'staging',
  'production',
  'local',
] as const;

type EnvironmentName = (typeof ENVIRONMENT_NAMES)[number];

export type Environment = {
  baseApiUrl: string;
  oauthConfig: AuthConfig;
  resourceServerUrls: string[];
  environment: EnvironmentName;
  albertaUrl: string;
};

/* Shape of /config.json. In a cluster the file comes from the web-config
   ConfigMap (helm/kea/templates/web-configmap.yaml), locally from
   apps/web/public/config.json. Public, non-secret values only. */
type RuntimeConfig = {
  environment: EnvironmentName;
  baseApiUrl: string;
  albertaUrl: string;
  oauth: {
    issuer: string;
    clientId: string;
  };
};

function requireNonEmptyString(value: unknown, key: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `Invalid /config.json: "${key}" must be a non-empty string`,
    );
  }
  return value;
}

function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('Invalid /config.json: expected a JSON object');
  }
  const value = raw as Record<string, unknown>;
  const oauth = (value['oauth'] ?? {}) as Record<string, unknown>;

  const environmentName = requireNonEmptyString(
    value['environment'],
    'environment',
  );
  if (!(ENVIRONMENT_NAMES as readonly string[]).includes(environmentName)) {
    throw new Error(
      `Invalid /config.json: "environment" must be one of ${ENVIRONMENT_NAMES.join(
        ', ',
      )}, got "${environmentName}"`,
    );
  }

  return {
    environment: environmentName as EnvironmentName,
    baseApiUrl: requireNonEmptyString(value['baseApiUrl'], 'baseApiUrl'),
    albertaUrl: requireNonEmptyString(value['albertaUrl'], 'albertaUrl'),
    oauth: {
      issuer: requireNonEmptyString(oauth['issuer'], 'oauth.issuer'),
      clientId: requireNonEmptyString(oauth['clientId'], 'oauth.clientId'),
    },
  };
}

async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load /config.json (HTTP ${response.status})`);
  }
  return parseRuntimeConfig(await response.json());
}

/* Assigned exactly once by loadEnvironment() before the app modules are
   evaluated: main.ts awaits loadEnvironment() and only then dynamically
   imports bootstrap.ts (Angular downlevels async/await for zone.js, so a
   top-level await here is not an option). Everything importing `environment`
   can keep reading it synchronously. Under Jest the module is replaced by
   the deterministic app.environment.mock.ts (see jest.config.ts). */
export let environment: Environment;

export async function loadEnvironment(): Promise<Environment> {
  const config = await loadRuntimeConfig();

  environment = {
    environment: config.environment,
    baseApiUrl: config.baseApiUrl,
    albertaUrl: config.albertaUrl,
    resourceServerUrls: [config.baseApiUrl],
    oauthConfig: {
      issuer: config.oauth.issuer,
      clientId: config.oauth.clientId,
      redirectUri: window.location.origin,
      responseType: 'code',
      scope: 'openid offline_access email profile',
      showDebugInformation: config.environment !== 'production',
      clearHashAfterLogin: true,
      preserveRequestedRoute: true,
    },
  };

  return environment;
}
